import raw from '../sources.json';
import type { Catalogue, Licence, SourceDef, TimeEntry } from './types';

export const catalogue = raw as unknown as Catalogue;

const byId = new Map<string, SourceDef>(catalogue.sources.map((s) => [s.id, s]));

export function source(id: string): SourceDef | undefined {
  return byId.get(id);
}

export function licenceOf(def: SourceDef): Licence {
  const l = catalogue.licences[def.licence];
  if (!l) throw new Error(`source ${def.id}: unknown licence ${def.licence}`);
  return l;
}

export function extentOf(def: SourceDef): [number, number, number, number] | undefined {
  return def.extent ? catalogue.extents[def.extent] : undefined;
}

export function defaultSource(): SourceDef {
  return catalogue.sources.find((s) => s.default) ?? catalogue.sources[0];
}

/** All epochs of a collection, including its parallel variants, flattened for the strip. */
export interface TimeSeries {
  key: string;
  label: string;
  times: TimeEntry[];
}

const VARIANT_LABELS: Record<string, string> = {
  mimovegetacni: 'mimovegetační',
  special: 'speciální',
};

export function seriesOf(def: SourceDef): TimeSeries[] {
  const out: TimeSeries[] = [];
  if (def.times?.length) out.push({ key: 'default', label: 'normální', times: def.times });
  for (const [key, times] of Object.entries(def.variants ?? {})) {
    out.push({ key, label: VARIANT_LABELS[key] ?? key, times });
  }
  return out;
}

export function findTime(def: SourceDef, timeId: string): TimeEntry | undefined {
  for (const s of seriesOf(def)) {
    const hit = s.times.find((t) => t.id === timeId);
    if (hit) return hit;
  }
  return undefined;
}

/** Metres per pixel for a source at a given epoch — the epoch wins where it is known. */
export function resolutionOf(def: SourceDef, timeId?: string): number | undefined {
  const t = timeId ? findTime(def, timeId) : undefined;
  return t?.res ?? def.resolution;
}

export function resolutionText(def: SourceDef, timeId?: string): string {
  const t = timeId ? findTime(def, timeId) : undefined;
  if (t?.res) return formatRes(t.res);
  return def.resolutionText;
}

export function formatRes(metres: number): string {
  if (metres >= 1000) return `${(metres / 1000).toLocaleString('cs')} km/px`;
  if (metres >= 1) return `${metres.toLocaleString('cs')} m/px`;
  // 12.5 cm must not read as 13 — the half centimetre is the difference between
  // ČÚZK's ortho and Prague's, and between one epoch and another.
  const cm = (metres * 100).toLocaleString('cs', { maximumFractionDigits: 1 });
  return `${cm} cm/px`;
}

/** Human label for an epoch, falling back to its id. */
export function epochLabel(def: SourceDef, timeId?: string): string | undefined {
  if (!timeId) return undefined;
  return findTime(def, timeId)?.label ?? timeId;
}

/**
 * The source as it behaves for one epoch: an epoch may point at a different
 * service entirely, so everything that talks to a service must resolve this first.
 */
export function effectiveDef(def: SourceDef, timeId?: string): SourceDef {
  const override = timeId ? findTime(def, timeId)?.override : undefined;
  return override ? { ...def, ...override } : def;
}

/** True when the service cannot be addressed without choosing an epoch. */
export function needsEpoch(def: SourceDef): boolean {
  return (
    def.url.includes('{time}') ||
    Boolean(def.wms?.layers.includes('{time}')) ||
    Boolean(def.arcgis?.layers.includes('{time}'))
  );
}

/**
 * Newest epoch of a collection — `times[]` runs oldest first, so that is the last
 * entry. Loads a remote epoch list where that is where the series lives.
 */
export async function newestEpochId(def: SourceDef): Promise<string | undefined> {
  const local = seriesOf(def)[0]?.times;
  if (local?.length) return local[local.length - 1].id;
  const remote = await loadRemoteTimes(def);
  return remote[remote.length - 1]?.id;
}

/** Newest epoch when it is already known locally, without a network round trip. */
export function newestLocalEpochId(def: SourceDef): string | undefined {
  const times = seriesOf(def)[0]?.times;
  return times?.length ? times[times.length - 1].id : undefined;
}

/** Live epoch list for sources that publish one (Esri Wayback). Cached per session. */
const remoteTimes = new Map<string, Promise<TimeEntry[]>>();

export function loadRemoteTimes(def: SourceDef): Promise<TimeEntry[]> {
  if (!def.timesFrom) return Promise.resolve([]);
  let p = remoteTimes.get(def.id);
  if (!p) {
    p = fetch(def.timesFrom)
      .then((r) => r.json())
      .then((cfg: Record<string, { itemTitle?: string }>) => {
        const out: TimeEntry[] = [];
        for (const [id, item] of Object.entries(cfg)) {
          const date = /(\d{4}-\d{2}-\d{2})/.exec(item.itemTitle ?? '')?.[1];
          if (date) out.push({ id, label: date });
        }
        // Same convention as the hand-written series: oldest first.
        out.sort((a, b) => a.label.localeCompare(b.label));
        return out;
      })
      .catch(() => []);
    remoteTimes.set(def.id, p);
  }
  return p;
}
